import type { ReactNode } from 'react'
import { ceilingVerdict, VerdictPill } from '../../shared/pills'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type {
  ReviewComment,
  ReviewVerification,
  VerificationCount,
  VerificationSubmit,
} from '../contracts'
import { CritiqueFlag } from '../critique-flag'

/**
 * Verification section (LG-637, spec T3/D18) — how many times the client ran,
 * how many times they submitted, and what actually failed. Back after LG-632
 * deleted the version that told every coach the same story: `RUNS = 5`,
 * `SUBMITS = 2`, targets out of `MOCK_CLIENT_PROFILE`, and a
 * "tests 3 & 5 failed — duplicate elements case" line describing a submission
 * that never existed.
 *
 * Every number here is read: the counts are frozen into the artifact when the
 * work qualified (`codingArtifactV2.counts`), and the failures are the cases
 * the client's own executions recorded (`submits[].failedTests`). A submission
 * whose snapshot carries no counts renders no section at all — absent rather
 * than a confident pair of zeroes, which is a different claim about the
 * client and a false one. That is the whole of this section's rule; the
 * exception for a review that already carries verification comments is
 * `SectionCard`'s to enforce (LG-821), which is why `hasContent` speaks only
 * about the counts.
 *
 * The targets are the other half of D18. They are real columns on the
 * engagement with a live mutation behind them, and they are null for every
 * engagement today — so the counts render bare, with no target line and no
 * verdict pill. That is the designed state, not a loading one: a green
 * "On target" measured against a target nobody set is a fabricated judgement,
 * and the empty space is the prompt to go set one.
 */

/**
 * The verdict rule is the shared `ceilingVerdict` (spec §8.5) — the same
 * thresholds behavioral's Delivery tiles judge by, and the ones the section
 * used before LG-632 deleted it. The thresholds were never the invented part,
 * only the numbers they were applied to.
 *
 * `composerTarget` is the label fragment `openComposer` files a tile's
 * comment under ("Verification · Runs") — display wording, nothing to do with
 * the coach-set numeric `target` ceiling on `VerificationCount`.
 */
const COUNT_TILES = [
  { key: 'runs', label: 'RUNS', composerTarget: 'Runs' },
  { key: 'submits', label: 'SUBMITS', composerTarget: 'Submits' },
] as const satisfies readonly {
  key: keyof Omit<ReviewVerification, 'failedSubmits'>
  label: string
  composerTarget: string
}[]

export function VerificationSection(props: {
  verification: ReviewVerification | null
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const { verification, comments = [], anchorRef } = props
  const { openComposer } = useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'verification')

  return (
    <SectionCard
      title="Verification"
      commentCount={sectionComments.length}
      hasContent={verification !== null}
      onAddComment={() =>
        openComposer('verification', 'Verification · Overall', 'critique')
      }
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      {verification !== null && (
        <>
          <div className="mb-2.5 grid grid-cols-2 gap-2.5">
            {COUNT_TILES.map((tile) => (
              <CountTile
                key={tile.key}
                count={verification[tile.key]}
                label={tile.label}
                onComment={() =>
                  openComposer(
                    'verification',
                    `Verification · ${tile.composerTarget}`,
                    'critique',
                    // Anchored on which count this is rather than on the
                    // label, for the same reason the complexity atoms are: the
                    // label is display text, and rewording it must not move a
                    // comment off the count it was written about.
                    `verification:${tile.key}`,
                  )
                }
              />
            ))}
          </div>

          {verification.failedSubmits.map((submit) => (
            <FailedSubmitRow key={submit.executionId} submit={submit} />
          ))}
        </>
      )}
    </SectionCard>
  )
}

/**
 * One count and, when the engagement set one, the ceiling it is judged
 * against. The target line and the pill live or die together: half of a
 * judgement is worse than none, since a bare "≤ 3" reads as a verdict the
 * coach never gets told the outcome of.
 */
function CountTile(props: {
  count: VerificationCount
  label: string
  onComment: () => void
}): ReactNode {
  const { count, label, onComment } = props
  return (
    <button
      type="button"
      onClick={onComment}
      className="rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-3 text-left"
    >
      <div className="flex items-baseline justify-between">
        <div className="text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
          {label}
        </div>
        {count.target !== null && (
          <VerdictPill verdict={ceilingVerdict(count.value, count.target)} />
        )}
      </div>
      <div className="mt-1 font-mono text-xl font-bold text-(--cd-text)">
        {count.value}
        {count.target !== null && (
          <span className="ml-1.5 font-sans text-xs font-medium text-(--cd-text-faint)">
            target ≤ {count.target}
          </span>
        )}
      </div>
    </button>
  )
}

/**
 * One graded attempt that did not pass, with the cases it got wrong.
 *
 * Test numbers are `index + 1` because `TestResult.index` is 0-based and the
 * client's own editor labels the same case "Test Case {index + 1}"
 * (`components/editor/test-results/raw-results-list.tsx`). A coach saying
 * "test 3 failed" has to mean the test the client saw fail.
 *
 * An attempt whose structured results were never stored has no cases to list,
 * and still renders: the counts alone say it did not pass, and dropping the
 * row would turn an unrecorded failure into no failure at all.
 */
function FailedSubmitRow(props: { submit: VerificationSubmit }): ReactNode {
  const { submit } = props
  return (
    <div className="mb-2 rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-3 last:mb-0">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="rounded-full px-2.5 py-0.5 text-[11px] font-bold"
          style={{
            color: 'var(--cd-red)',
            background: 'color-mix(in oklab, var(--cd-red) 12%, transparent)',
          }}
        >
          Submit #{submit.attempt}
        </span>
        <span className="text-[11.5px] text-(--cd-text-muted)">
          {submit.passed}/{submit.total} passed
        </span>
      </div>
      {submit.failedTests.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1">
          {submit.failedTests.map((test) => (
            <li
              key={test.index}
              className="text-[11.5px] leading-relaxed text-(--cd-text-2)"
            >
              <span className="font-semibold">Test {test.index + 1}</span>
              {test.reason !== null && (
                <span className="text-(--cd-text-muted)"> — {test.reason}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
